<script lang="ts">
  import { STRINGS } from '$lib/constants/strings'
  import { Divider, Select, type Language, type Theme } from '@bond/components'
  import { LANGUAGE_NAMES } from '$lib/constants/lang'
  import { utils } from '$lib/utils'
  import { page } from '$app/stores'
  import type { Writable } from 'svelte/store'

  export let lang: Language = 'en'
  export let src: string | undefined = undefined
  export let storedTheme: Theme | undefined = undefined
  export let theme: Writable<Theme | null>

  $: localeRoute = utils.getRoute(lang)
</script>

<footer>
  <nav>
    {#if src}
      <div class="logo">
        <a href={localeRoute('/')} title={STRINGS[lang].FOOTER_LOGO_TITLE}>
          <picture>
            {#if storedTheme || $theme}
              <source srcset={src} />
            {/if}
            <source
              srcset="/images/bruce-studios-logo-white.svg"
              media="(prefers-color-scheme: dark)"
            />
            <source
              srcset="/images/bruce-studios-logo-black.svg"
              media="(prefers-color-scheme: light)"
            />
            <img {src} alt={STRINGS[lang].FOOTER_LOGO_ALT} />
          </picture>
        </a>
      </div>
    {/if}
    <ul>
      <li>
        <a href={localeRoute('/become-a-partner')}
          >{STRINGS[lang].FOOTER_BECOME_A_PARTNER}</a
        >
      </li>
      <li>
        <a href={localeRoute('/occupational-health')}>
          {STRINGS[lang].FOOTER_OCCUPATIONAL_HEALTH}
        </a>
      </li>
      <li>
        <a href="https://bruce.teamtailor.com">{STRINGS[lang].FOOTER_CAREERS}</a
        >
      </li>
      <li>
        <a href={localeRoute('/about')}>{STRINGS[lang].FOOTER_ABOUT_US}</a>
      </li>
      <li>
        <a href={localeRoute('/gift-card')}>{STRINGS[lang].FOOTER_GIFT_CARDS}</a
        >
      </li>
      <li>
        <a href={localeRoute('/follow-us')}>{STRINGS[lang].FOOTER_FOLLOW_US}</a>
      </li>
    </ul>
  </nav>
  <Divider />
  <section class="legal">
    <div>
      <div>
        © {new Date().toLocaleDateString(undefined, { year: 'numeric' })} Bruce
        Studios. All rights reserved.
      </div>
      <ul>
        <li>
          <a href={localeRoute('/terms')}>{STRINGS[lang].FOOTER_TERMS}</a>
        </li>
        <li>
          <a href={localeRoute('/privacy')}>{STRINGS[lang].FOOTER_PRIVACY}</a>
        </li>
      </ul>
    </div>
    <Select
      variant="outlined"
      rounded
      appendIcon="ph-globe"
      on:change={(event) => utils.changeLanguage(event, $page.url)}
      value={lang}
    >
      {#each Object.entries(LANGUAGE_NAMES) as [key, value] (key)}
        <option value={key}>{value}</option>
      {/each}
    </Select>
  </section>
</footer>

<style lang="scss">
  @use '@bond/assets/css/breakpoints';

  footer {
    box-sizing: border-box;
    bottom: 0;
    width: 100%;
    padding: 2.5em 5.5em;
    background-color: var(--footer-background-color);
    color: var(--footer-color);
    font-size: 18px;

    @media screen and (max-width: breakpoints.$breakpoint-md) {
      padding: 1.5em;
    }

    nav {
      display: flex;
      gap: 16em;
      margin-bottom: 2em;

      .logo {
        @media screen and (max-width: breakpoints.$breakpoint-md) {
          display: none;
        }

        img {
          height: 30px;
        }
      }

      ul {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, auto));
        grid-template-rows: repeat(2, minmax(0, 1fr));
        grid-auto-flow: column;
        gap: 1em;
        list-style-type: none;
        margin: 0;
        padding: 0;
        flex: 1;

        @media screen and (max-width: breakpoints.$breakpoint-md) {
          display: flex;
          flex-direction: column;
        }

        li {
          a {
            text-decoration: none;

            &:hover,
            &:focus {
              text-decoration: underline;
            }
          }
        }
      }
    }

    .legal {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1em;
      margin: 2em 0;

      @media screen and (max-width: breakpoints.$breakpoint-md) {
        flex-direction: column-reverse;
        align-items: flex-start;
        font-size: 16px;
      }

      *:first-child {
        display: flex;
        gap: 1em;

        @media screen and (max-width: breakpoints.$breakpoint-md) {
          flex-direction: column-reverse;
        }
      }

      ul {
        display: flex;
        list-style-type: none;
        margin: 0;
        padding: 0;
        gap: 1em;

        li {
          a {
            text-decoration: none;

            &:hover,
            &:focus {
              text-decoration: underline;
            }
          }
        }
      }
    }
  }
</style>
